import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Calendar, FileText, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import { useI18n } from '../i18n';

import { API_BASE } from '../config/api';

interface CurrentDoctor {
    id: string;
    firstName: string;
    lastName: string;
    specialty: string | null;
}

interface DashboardStats {
    patientCount: number;
    todayAppointments: number;
    totalEncounters: number;
    pendingInvoices: number;
}

interface TodayAppointment {
    id: string;
    scheduledStart: string;
    status: string;
    serviceType: string;
    appointmentType: string;
    patient: { id: string; firstName: string; lastName: string };
    doctor: { id: string; firstName: string; lastName: string };
}

export function DashboardPage() {
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const [stats, setStats] = useState<DashboardStats>({ patientCount: 0, todayAppointments: 0, totalEncounters: 0, pendingInvoices: 0 });
    const [todayAppts, setTodayAppts] = useState<TodayAppointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentDoctor, setCurrentDoctor] = useState<CurrentDoctor | null>(null);

    const today = new Date().toLocaleDateString(locale === 'es' ? 'es-CO' : 'en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    const dateStr = new Date().toISOString().split('T')[0];

    const greeting = (() => {
        const hour = new Date().getHours();
        if (hour < 12) return t('dashboard.greeting.morning');
        if (hour < 18) return t('dashboard.greeting.afternoon');
        return t('dashboard.greeting.evening');
    })();

    useEffect(() => {
        // In demo/dev mode, get the first doctor as the logged-in doctor
        fetch(`${API_BASE}/staff?role=doctor`)
            .then(r => r.json())
            .then(res => {
                const doc = (res.data || [])[0];
                if (doc) setCurrentDoctor(doc);
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        if (currentDoctor) loadDashboard(currentDoctor.id);
    }, [currentDoctor, dateStr]);

    async function loadDashboard(doctorId: string) {
        setLoading(true);
        try {
            const [statsRes, apptsRes] = await Promise.all([
                fetch(`${API_BASE}/stats/dashboard?doctorId=${doctorId}`).then(r => r.json()),
                fetch(`${API_BASE}/appointments?date=${dateStr}&doctorId=${doctorId}&limit=20`).then(r => r.json()),
            ]);
            setStats(statsRes.data);
            setTodayAppts(apptsRes.data || []);
        } catch {
            // Fallback - API might not be running
            console.warn('Dashboard API unavailable, showing defaults');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-content fade-in">
            <div className="page-header" style={{ position: 'static', borderBottom: 'none', background: 'transparent', paddingLeft: 0 }}>
                <div>
                    <h1 className="page-title">{greeting}, {currentDoctor ? `${currentDoctor.firstName} ${currentDoctor.lastName}` : t('dashboard.doctor')}</h1>
                    <p className="page-subtitle" style={{ textTransform: 'capitalize' }}>{today}</p>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon stat-icon-primary"><Calendar size={24} /></div>
                    <div>
                        <div className="stat-value">{loading ? '...' : stats.todayAppointments}</div>
                        <div className="stat-label">{t('dashboard.today_appointments')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon stat-icon-success"><Users size={24} /></div>
                    <div>
                        <div className="stat-value">{loading ? '...' : stats.patientCount}</div>
                        <div className="stat-label">{t('dashboard.active_patients')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon stat-icon-warning"><FileText size={24} /></div>
                    <div>
                        <div className="stat-value">{loading ? '...' : stats.totalEncounters}</div>
                        <div className="stat-label">{t('nav.encounters')}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon stat-icon-danger"><AlertCircle size={24} /></div>
                    <div>
                        <div className="stat-value">{loading ? '...' : stats.pendingInvoices}</div>
                        <div className="stat-label">{t('dashboard.pending_invoices')}</div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                {/* Today's Schedule */}
                <div className="card">
                    <div className="card-header">
                        <h2 className="card-title">{t('dashboard.today_agenda')}</h2>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/appointments')}>{t('dashboard.view_all')}</button>
                    </div>
                    <div className="table-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>{t('appointments.time')}</th>
                                    <th>{t('appointments.patient')}</th>
                                    <th>{t('appointments.type')}</th>
                                    <th>{t('appointments.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 3 }).map((_, i) => (
                                        <tr key={i}>
                                            {Array.from({ length: 4 }).map((_, j) => (
                                                <td key={j}><div className="skeleton" style={{ height: 18, width: '80%' }} /></td>
                                            ))}
                                        </tr>
                                    ))
                                ) : todayAppts.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
                                            {t('dashboard.no_appointments_today')}
                                        </td>
                                    </tr>
                                ) : (
                                    todayAppts.slice(0, 8).map((appt) => {
                                        const time = new Date(appt.scheduledStart).toLocaleTimeString(locale === 'es' ? 'es-CO' : 'en-US', { hour: '2-digit', minute: '2-digit' });
                                        return (
                                            <tr key={appt.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/appointments')}>
                                                <td style={{ fontWeight: 500 }}>{time}</td>
                                                <td>{appt.patient.firstName} {appt.patient.lastName}</td>
                                                <td>{t(`service.${appt.serviceType}`) !== `service.${appt.serviceType}` ? t(`service.${appt.serviceType}`) : appt.serviceType}</td>
                                                <td><span className={`badge ${(
                                                    appt.status === 'booked' ? 'badge-neutral' :
                                                        appt.status === 'confirmed' ? 'badge-primary' :
                                                            appt.status === 'arrived' ? 'badge-warning' :
                                                                appt.status === 'in_progress' ? 'badge-primary' :
                                                                    appt.status === 'completed' ? 'badge-success' :
                                                                        'badge-danger'
                                                )}`}>{t(`appointments.status.${appt.status}`)}</span></td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="card">
                    <div className="card-header">
                        <h2 className="card-title">{t('dashboard.quick_actions')}</h2>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        {[
                            { icon: Users, text: t('dashboard.action.register_patient'), path: '/patients', color: 'var(--color-primary)' },
                            { icon: Calendar, text: t('dashboard.action.schedule_appointment'), path: '/appointments', color: 'var(--color-secondary)' },
                            { icon: FileText, text: t('dashboard.action.view_encounters'), path: '/encounters', color: 'var(--color-success)' },
                            { icon: TrendingUp, text: t('dashboard.action.lab_results'), path: '/patients', color: 'var(--color-accent)' },
                        ].map((item, i) => (
                            <button
                                key={i}
                                onClick={() => navigate(item.path)}
                                className="btn btn-secondary"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                    justifyContent: 'flex-start', padding: 'var(--space-3) var(--space-4)',
                                    textAlign: 'left', width: '100%',
                                }}
                            >
                                <div style={{
                                    width: 32, height: 32, borderRadius: 'var(--radius-md)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    background: `${item.color}15`, color: item.color, flexShrink: 0,
                                }}>
                                    <item.icon size={16} />
                                </div>
                                <span style={{ fontSize: 'var(--text-sm)' }}>{item.text}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
